import React from "react";

// ============================================================================
// CSS ANIMATIONS for Chrome Extension illustration
// ============================================================================

const ceIllustrationStyles = `
@keyframes ceCursorMove {
  0%, 100% { transform: translate(14px, -10px); opacity: 0.3; }
  30%, 70% { transform: translate(0, 0); opacity: 1; }
  45%, 55% { transform: translate(0.5px, 0.5px); opacity: 1; }
}
@keyframes ceClickRipple {
  0%, 30% { transform: scale(0.8); opacity: 0; }
  40% { transform: scale(1); opacity: 0.5; }
  55% { transform: scale(1.4); opacity: 0; }
  100% { transform: scale(0.8); opacity: 0; }
}
@keyframes ceParticle {
  0% { transform: translateY(0) scale(1); opacity: 0; }
  15% { opacity: 1; }
  100% { transform: translateY(-28px) scale(0); opacity: 0; }
}
`;

if (typeof document !== 'undefined') {
  const styleId = 'ce-illustration-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = ceIllustrationStyles;
    document.head.appendChild(style);
  }
}

// ============================================================================
// Chrome Extension Illustration
// ============================================================================

const ChromeExtensionIllustration = () => (
  <div className="relative w-full max-w-[260px] mx-auto">
    {/* Mini browser window */}
    <div className="rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/70 overflow-hidden shadow-md">
      {/* Browser chrome */}
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-gray-100 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-800/60">
        <div className="flex gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-red-400/60" />
          <div className="w-1.5 h-1.5 rounded-full bg-yellow-400/60" />
          <div className="w-1.5 h-1.5 rounded-full bg-green-400/60" />
        </div>
        <div className="flex-1 ml-1.5 h-4 rounded-md bg-gray-100 dark:bg-gray-700/50 flex items-center px-2">
          <span className="text-[6px] text-gray-400 dark:text-gray-500 font-medium tracking-wide">example.com/products</span>
        </div>
        {/* Extension icon in toolbar */}
        <div className="w-4 h-4 rounded bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-sm">
          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        </div>
      </div>

      {/* Page content with selected elements */}
      <div className="p-2.5 space-y-1.5">
        {[
          { w: "w-4/5" },
          { w: "w-3/5" },
          { w: "w-2/3" },
        ].map((row, i) => (
          <div
            key={i}
            className="flex items-center gap-2 p-1.5 rounded-lg border border-cyan-400/30 bg-cyan-50/40 dark:bg-cyan-500/[0.04] dark:border-cyan-500/15"
          >
            <div className="w-4 h-4 rounded bg-gray-200/80 dark:bg-gray-700/60 flex-shrink-0" />
            <div className="flex-1 space-y-0.5">
              <div className={`h-1.5 ${row.w} rounded-full bg-gray-300/80 dark:bg-gray-600`} />
              <div className="h-1 w-2/5 rounded-full bg-gray-200/80 dark:bg-gray-700" />
            </div>
            <div className="w-3 h-3 rounded-full bg-cyan-500/20 dark:bg-cyan-500/15 flex items-center justify-center flex-shrink-0">
              <svg className="w-2 h-2 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        ))}
      </div>

      {/* Extension action bar */}
      <div className="flex items-center gap-2 px-2.5 py-2 border-t border-gray-100 dark:border-gray-700/40 bg-gray-50/60 dark:bg-gray-800/30">
        <span className="text-[7px] font-medium text-gray-400 dark:text-gray-500">3 items selected</span>
        <div className="flex-1" />
        {/* Cloud button */}
        <div className="relative">
          <div className="px-2 py-1 rounded-md bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 flex items-center gap-1 shadow-sm shadow-cyan-500/25">
            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
            </svg>
            <span className="text-[7px] font-bold text-white tracking-wide">Run in Cloud</span>
          </div>
          {/* Click ripple */}
          <div
            className="absolute -inset-1 rounded-lg border border-cyan-400/30"
            style={{ animation: 'ceClickRipple 3.5s ease-in-out infinite' }}
          />
          {/* Particles floating up */}
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="absolute -top-1 rounded-full bg-cyan-400"
              style={{
                width: 3,
                height: 3,
                left: `${30 + i * 25}%`,
                animation: `ceParticle 3.5s ease-out ${0.4 + i * 0.15}s infinite`,
                opacity: 0,
              }}
            />
          ))}
        </div>
      </div>
    </div>

    {/* Animated cursor */}
    <div
      className="absolute bottom-2 right-4 pointer-events-none"
      style={{ animation: 'ceCursorMove 3.5s ease-in-out infinite' }}
    >
      <svg width="11" height="15" viewBox="0 0 14 18" fill="none">
        <path
          d="M1 1L1 14L4.5 10.5L7.5 17L10 16L7 9L12 8.5L1 1Z"
          className="fill-white dark:fill-gray-200"
          stroke="black"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  </div>
);

export default ChromeExtensionIllustration;
